/** Where pdf.js fetches what it can't bundle: image decoders, CMaps, standard fonts and ICC profiles. */
export type PdfjsAssetUrls = {
	wasmUrl: string;
	cMapUrl: string;
	standardFontDataUrl: string;
	iccUrl: string;
};

type Core = typeof import("pdfjs-dist/legacy/build/pdf.mjs");

/**
 * The pdf.js core (documents), its viewer components (pages, find, links), its asset URLs, and
 * one worker every document loads in, so a new document doesn't start and parse a worker again.
 */
export type Pdfjs = {
	core: Core;
	components: typeof import("pdfjs-dist/legacy/web/pdf_viewer.mjs");
	assets: PdfjsAssetUrls;
	worker: InstanceType<Core["PDFWorker"]>;
};

/**
 * The `pdfjsAssets` Vite plugin serves `pdfjs-dist`'s asset folders under this versioned path.
 * Absolute, since the worker fetches them and would resolve a relative path against its own URL.
 */
const assetUrls = (version: string): PdfjsAssetUrls => {
	const base = new URL(`${import.meta.env.BASE_URL}pdfjs/${version}/`, document.baseURI).href;
	return {
		wasmUrl: `${base}wasm/`,
		cMapUrl: `${base}cmaps/`,
		standardFontDataUrl: `${base}standard_fonts/`,
		iccUrl: `${base}iccs/`,
	};
};

let loading: Promise<Pdfjs> | undefined;

/**
 * Loads pdf.js on first use, so screens without a document don't download it. Every import
 * stays dynamic, or the bundler preloads the whole chunk. The viewer components read
 * `globalThis.pdfjsLib`, which the core sets, so the core loads first. The legacy build carries
 * polyfills for the newest JS APIs pdf.js calls, which the modern build assumes the browser has.
 */
export const loadPdfjs = (): Promise<Pdfjs> => {
	loading ??= (async () => {
		const [core, worker] = await Promise.all([
			import("pdfjs-dist/legacy/build/pdf.mjs"),
			import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url"),
		]);
		core.GlobalWorkerOptions.workerSrc = worker.default;
		const [components] = await Promise.all([
			import("pdfjs-dist/legacy/web/pdf_viewer.mjs"),
			import("pdfjs-dist/legacy/web/pdf_viewer.css"),
		]);
		return {
			core,
			components,
			assets: assetUrls(core.version),
			worker: new core.PDFWorker(),
		};
	})().catch((error: unknown) => {
		loading = undefined;
		throw error;
	});
	return loading;
};
